import React, { useState } from 'react';
import { GitMerge, Clock, Users, CheckCircle2, AlertTriangle, Laptop, Monitor, X } from 'lucide-react';
import type { ConflictVoteRecord, FieldConflict } from '../../../types';
import { formatFieldValue } from '../utils/threeWayMerge';

interface Props {
  votes: ConflictVoteRecord[];
  currentUserId: string;
  escalatedVoteId: string | null;
  onSubmitVote: (voteId: string, choices: Record<string, 'local' | 'server'>) => Promise<void>;
  onApplyResolution: (vote: ConflictVoteRecord) => Promise<void>;
  onAbort: (voteId: string) => Promise<void>;
  onDismissEscalation: () => void;
}

function DeadlineCountdown({ deadline }: { deadline: string }) {
  const remaining = new Date(deadline).getTime() - Date.now();
  if (remaining <= 0) return <span className="text-red-500 font-semibold">Deadline passed</span>;
  const hours = Math.floor(remaining / 3600000);
  const mins = Math.floor((remaining % 3600000) / 60000);
  return (
    <span className="text-slate-500 dark:text-slate-400">
      {hours > 0 ? `${hours}h ` : ''}{mins}m remaining
    </span>
  );
}

function VoteTally({ fields, votes }: { fields: FieldConflict[]; votes: ConflictVoteRecord['votes'] }) {
  if (votes.length === 0) return <p className="text-xs text-slate-500 italic">No votes yet</p>;
  return (
    <div className="space-y-1.5 mt-2">
      {fields.map((f) => {
        const localCount = votes.filter((v) => v.choices[f.field] === 'local').length;
        const serverCount = votes.filter((v) => v.choices[f.field] === 'server').length;
        const total = votes.length;
        return (
          <div key={f.field} className="flex items-center gap-2 text-xs">
            <span className="w-24 truncate font-medium text-slate-600 dark:text-slate-300">{f.label}</span>
            <div className="flex-1 flex items-center gap-1">
              <span className="text-indigo-600 dark:text-indigo-400 font-semibold">{localCount}</span>
              <div className="flex-1 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                <div
                  className="h-full bg-indigo-500 rounded-full transition-all"
                  style={{ width: total > 0 ? `${(localCount / total) * 100}%` : '0%' }}
                />
              </div>
              <span className="text-emerald-600 dark:text-emerald-400 font-semibold">{serverCount}</span>
            </div>
          </div>
        );
      })}
      <p className="text-[11px] text-slate-400 mt-1">
        <span className="text-indigo-500">■</span> offline &nbsp;
        <span className="text-emerald-500">■</span> server
      </p>
    </div>
  );
}

export const ConflictVotePanel: React.FC<Props> = ({
  votes,
  currentUserId,
  escalatedVoteId,
  onSubmitVote,
  onApplyResolution,
  onAbort,
  onDismissEscalation,
}) => {
  const [activeVoteId, setActiveVoteId] = useState<string | null>(votes[0]?.id ?? null);
  const [choices, setChoices] = useState<Record<string, Record<string, 'local' | 'server'>>>({});
  const [submitting, setSubmitting] = useState(false);

  const activeVote = votes.find((v) => v.id === activeVoteId) ?? votes[0];
  if (!activeVote) return null;

  const isInitiator = activeVote.initiatorUserId === currentUserId;
  const myVote = activeVote.votes.find((v) => v.userId === currentUserId);
  const isEscalated = escalatedVoteId === activeVote.id;
  const deadlinePassed = new Date(activeVote.deadline) < new Date();
  const nonInitiatorVotes = activeVote.votes.filter((v) => v.userId !== activeVote.initiatorUserId);
  const currentChoices = choices[activeVote.id] ?? {};

  const setChoice = (field: string, choice: 'local' | 'server') => {
    setChoices((prev) => ({
      ...prev,
      [activeVote.id]: { ...prev[activeVote.id], [field]: choice },
    }));
  };

  const allChosen = activeVote.fields.every((f) => currentChoices[f.field] !== undefined);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await onSubmitVote(activeVote.id, currentChoices);
    } finally {
      setSubmitting(false);
    }
  };

  // Tally majority resolution for display/apply
  const majorityResolution: Record<string, 'local' | 'server'> = {};
  for (const f of activeVote.fields) {
    const localVotes = activeVote.votes.filter((v) => v.choices[f.field] === 'local').length;
    const serverVotes = activeVote.votes.filter((v) => v.choices[f.field] === 'server').length;
    majorityResolution[f.field] = localVotes >= serverVotes ? 'local' : 'server';
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="w-full max-w-2xl max-h-[90vh] flex flex-col rounded-xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-800">
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200 dark:border-slate-700">
          <Users className="w-5 h-5 text-violet-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <h2 className="font-bold text-slate-800 dark:text-slate-100 text-base">Team conflict vote</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 truncate">
              <span className="capitalize">{activeVote.entityType}</span>:{' '}
              <span className="font-medium text-slate-700 dark:text-slate-300">{activeVote.entityTitle}</span>
              {' · '}
              <DeadlineCountdown deadline={activeVote.deadline} />
            </p>
          </div>
          {votes.length > 1 && (
            <div className="flex items-center gap-1">
              {votes.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setActiveVoteId(v.id)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    v.id === activeVote.id ? 'bg-violet-500' : 'bg-slate-300 dark:bg-slate-600'
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Escalation banner */}
        {isEscalated && (
          <div className="px-5 py-3 bg-red-50 dark:bg-red-950/30 border-b border-red-200 dark:border-red-800 flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-700 dark:text-red-300">No one voted before the deadline</p>
              <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">
                Reach out to your team to resolve this, or abort your offline changes for this item.
              </p>
            </div>
            <button type="button" onClick={onDismissEscalation} className="text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="px-5 py-3 bg-violet-50 dark:bg-violet-950/30 border-b border-violet-200 dark:border-violet-800">
          <div className="flex items-center gap-2">
            <GitMerge className="w-4 h-4 text-violet-500" />
            <p className="text-xs text-violet-800 dark:text-violet-300">
              {isInitiator
                ? `Started by you · ${nonInitiatorVotes.length} teammate vote${nonInitiatorVotes.length !== 1 ? 's' : ''} so far`
                : `Started by ${activeVote.initiatorUsername} · ${nonInitiatorVotes.length} vote${nonInitiatorVotes.length !== 1 ? 's' : ''} so far`}
            </p>
          </div>
          {activeVote.votes.length > 0 && (
            <VoteTally fields={activeVote.fields} votes={activeVote.votes} />
          )}
        </div>

        <div className="flex-1 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
          {isInitiator && (myVote || deadlinePassed) ? (
            /* Initiator view after votes are in or deadline passed */
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-700 dark:text-slate-300">
                {deadlinePassed
                  ? 'The voting deadline has passed. You can apply the majority result or abort your changes.'
                  : 'You sent this to a vote. Apply the team\'s majority result or wait for more votes.'}
              </p>
              <div className="space-y-3">
                {activeVote.fields.map((f) => (
                  <div key={f.field} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 space-y-1">
                    <p className="text-xs font-bold text-slate-600 dark:text-slate-400">{f.label}</p>
                    <div className="flex items-center gap-2 text-xs">
                      <span className={`px-2 py-0.5 rounded font-semibold ${
                        majorityResolution[f.field] === 'local'
                          ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300'
                          : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300'
                      }`}>
                        {majorityResolution[f.field] === 'local' ? (
                          <><Laptop className="w-3 h-3 inline mr-1" />Offline wins</>
                        ) : (
                          <><Monitor className="w-3 h-3 inline mr-1" />Server wins</>
                        )}
                      </span>
                      <span className="text-slate-400 truncate">{formatFieldValue(
                        majorityResolution[f.field] === 'local' ? f.localValue : f.serverValue
                      )}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : !isInitiator && myVote ? (
            /* Non-initiator already voted */
            <div className="p-5 text-center space-y-2">
              <CheckCircle2 className="w-8 h-8 text-emerald-500 mx-auto" />
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Your vote is recorded</p>
              <p className="text-xs text-slate-500">Waiting for {activeVote.initiatorUsername} to apply the result.</p>
            </div>
          ) : (
            /* Voter: field-by-field choice */
            activeVote.fields.map((f) => (
              <div key={f.field} className="p-4 space-y-3">
                <p className="text-xs font-bold text-slate-700 dark:text-slate-300">{f.label}</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setChoice(f.field, 'local')}
                    className={`text-left p-3 rounded-lg border-2 transition-all ${
                      currentChoices[f.field] === 'local'
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/40'
                        : 'border-slate-200 dark:border-slate-700 hover:border-indigo-300'
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Laptop className="w-3.5 h-3.5 text-indigo-500" />
                      <span className="text-[11px] font-semibold text-indigo-700 dark:text-indigo-400">
                        {activeVote.initiatorUsername}'s offline version
                      </span>
                      {currentChoices[f.field] === 'local' && (
                        <CheckCircle2 className="w-3.5 h-3.5 text-indigo-500 ml-auto" />
                      )}
                    </div>
                    <p className="text-xs text-slate-700 dark:text-slate-300 break-words line-clamp-4">
                      {formatFieldValue(f.localValue)}
                    </p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setChoice(f.field, 'server')}
                    className={`text-left p-3 rounded-lg border-2 transition-all ${
                      currentChoices[f.field] === 'server'
                        ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/40'
                        : 'border-slate-200 dark:border-slate-700 hover:border-emerald-300'
                    }`}
                  >
                    <div className="flex items-center gap-1.5 mb-1.5">
                      <Monitor className="w-3.5 h-3.5 text-emerald-500" />
                      <span className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">Server version</span>
                      {currentChoices[f.field] === 'server' && (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 ml-auto" />
                      )}
                    </div>
                    <p className="text-xs text-slate-700 dark:text-slate-300 break-words line-clamp-4">
                      {formatFieldValue(f.serverValue)}
                    </p>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={() => void onAbort(activeVote.id)}
            className="px-4 py-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 text-sm font-semibold transition-colors"
          >
            Abort changes
          </button>

          {isInitiator ? (
            <button
              type="button"
              onClick={() => void onApplyResolution({ ...activeVote, resolution: majorityResolution })}
              className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold transition-colors flex items-center gap-1.5"
            >
              <CheckCircle2 className="w-4 h-4" />
              Apply team result
            </button>
          ) : !myVote ? (
            <button
              type="button"
              disabled={!allChosen || submitting}
              onClick={() => void handleSubmit()}
              className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 disabled:opacity-50 text-white text-sm font-semibold transition-colors flex items-center gap-1.5"
            >
              <Clock className="w-4 h-4" />
              {submitting ? 'Submitting…' : 'Submit vote'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};
